import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircleIcon } from '@heroicons/react/solid';
import { Server } from '@wajeez-web/core/api-base';
import {
  Card,
  InputLabel,
  CallBackSelector,
  Button,
  ButtonHeadless,
  CheckBox,
  NumberInput,
} from '@wajeez-web/components/ui';
import {
  HasPermissionWrapperComponent,
  useContentEntityListsByContentEntityId,
  useGetContentEntityBadge,
  useGetPublishersLookups,
  useSetContentEntityBadge,
  useSetContentEntityListsItemList,
  useSetContentEntityPublisher,
  useSetContentEntityVoiceActor,
} from '@wajeez-web/components/helpers';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { InnerPageToggleButton } from '@wajeez-web/components/ui';
import AuthorsModalComponent from './AuthorModalComponent';
import {
  ContentEntityTypeActions,
  AuthorsActions,
  ContentEntityActions,
  useContentSelector,
  useContentDispatch,
  TagsActions,
  getContentEntityListsLookups,
  getContentEntityListsByContentEntityId,
  setContentEntityListsItemList,
  clearContentEntityListsByContentEntityId,
  createContentEntityMultilingual,
  createAuthorMultilingual,
  getContentEntityBadge,
  clearContentEntityBadge,
  getBadgesLookups,
  setContentEntityBadge,
  createBadge,
  getPublishersLookups,
  setContentEntityPublisher,
  createPublisher,
  getVoiceActorsLookups,
  setContentEntityVoiceActor,
} from '@wajeez-web/stores/content';
import {
  useCommonDispatch,
  useCommonSelector,
} from '@wajeez-web/stores/common';
import ContentEntityFormModal from './ContentEntityFormModal';
import { BiLoaderAlt } from 'react-icons/bi';
import TagModal from './TagModal';
import { ContentEntityListModal } from './ContentEntityListModal';
import { useAuthSelector } from '@wajeez-web/stores/auth';
import BadgeModal from './BadgeModal';
import PublisherModal from './PublisherModal';
import VoiceActorModal from './VoiceActorModal';

interface CreateContentItemFormPageProps {
  allowedInEntityTypes?: Server.AllowedEntityTypeOutput[];
  requireAuthor?: boolean;
  id: string;
  type: string;
}

const GetContentEntityComponent = ({
  ContentItemType,
  data,
  id,
  afterChange,
}: {
  ContentItemType: string;
  data?: Server.ContentEntityTypeLookUpOutput;
  id: string;
  afterChange?: () => void | Promise<void>;
}) => {
  const modalDetails = useContentSelector(
    (s) => s.contentEntityType.modalDetails
  );
  const dispatch = useCommonDispatch();
  const { updatingStatus, assignStatus, createMultilingual } =
    useContentSelector((s) => s.contentEntity);
  const defaultValues = useContentSelector(
    (s) =>
      s.contentEntity.contentParents
        ?.filter((s) => s.contentEntityType == ContentItemType)
        .map((s) => ({ label: s.title || '', value: s.id || '' })) || [],
    (a, b) => {
      // check for length and ids
      if (a.length != b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i].value != b[i].value) return false;
      }
      return true;
    }
  );

  const [selectorValues, setSelectorValues] = useState<
    { label: string; value: string }[]
  >([]);
  // const [values, setValues] = useState<{ label: string; value: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSearch = (searchInput: string) => {
    return ContentEntityActions.searchContentEntityPaginatedForSelector({
      ContentEntityTypeNameSearchText: searchInput,
      ContentItemType: ContentItemType,
      page: 1,
      pageSize: 100,
    }).then((res) => {
      return res?.map((item) => ({
        label: item.title,
        value: item.id,
      }));
    });
  };

  const isDirty = () => {
    return !(
      Array.isArray(defaultValues) &&
      Array.isArray(selectorValues) &&
      defaultValues.length === selectorValues.length &&
      defaultValues.every((v, i) => v.value === selectorValues[i].value)
    );
  };

  // useCallbackPrompt(true);

  useEffect(() => {
    if (defaultValues) {
      const arr = defaultValues;
      setSelectorValues(arr);
      // setValues(arr);
    }
  }, [defaultValues]);

  const openModal = () => {
    dispatch(
      ContentEntityTypeActions.getContentEntityTypeByNameForModalAsync({
        name: ContentItemType,
      })
    ).then(() => {
      setShowForm(true);
    });
  };

  const onSave = async () => {
    setSaving(true);

    try {
      const valuesToUnassign = defaultValues?.filter(
        (item) => !selectorValues.find((i) => i.value === item.value)
      );

      for (const item of valuesToUnassign) {
        await dispatch(
          ContentEntityActions.unassignContentEntityAsync({
            itemId: id,
            collectionId: item.value,
            languageId: 1,
          })
        );
      }

      const valuesToAssign = selectorValues?.filter(
        (item) => !defaultValues?.find((i) => i.value === item.value)
      );

      for (const item of valuesToAssign) {
        await dispatch(
          ContentEntityActions.assignContentEntityAsync({
            itemId: id,
            collectionId: item.value,
            languageId: 1,
          })
        );
      }

      setSaving(false);
      await afterChange?.();
    } catch (error) {
      setSaving(false);

      await afterChange?.();
    }
  };

  return (
    <div key={ContentItemType} className="col-span-2 md:col-span-1 ">
      <div className="flex justify-between">
        <InputLabel name={ContentItemType} text={data?.displayNameSingular} />
        <HasPermissionWrapperComponent permissions={['27.1', '28.1']} multiple>
          <Button
            color="primary"
            Icon={PlusCircleIcon}
            type="text"
            onClick={() => openModal()}
          />
        </HasPermissionWrapperComponent>
      </div>
      <div className="flex space-s-1">
        <div className="w-full">
          <CallBackSelector
            key={ContentItemType}
            defaultValue={defaultValues}
            value={selectorValues}
            onSearch={onSearch}
            onChange={(name, value: any) => setSelectorValues(value)}
            isMulti={true}
            name={ContentItemType}
          />
        </div>
        <HasPermissionWrapperComponent permissions={['28.1', '28.4']}>
          <Button
            loading={saving}
            disabled={!isDirty()}
            title={'save'}
            onClick={() => onSave()}
          />
        </HasPermissionWrapperComponent>
      </div>
      {showForm && modalDetails && (
        <ContentEntityFormModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          data={modalDetails}
          loading={
            createMultilingual.status == 'loading' || assignStatus == 'loading'
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onSubmit={async (data: Server.CreateContentEntityByTypeIdV2Input) => {
            createContentEntityMultilingual(data, (response) => {
              dispatch(
                ContentEntityActions.assignContentEntityAsync({
                  languageId: 1,
                  itemId: id,
                  collectionId: response || '',
                })
              ).then((res) => {
                if (res.meta.requestStatus === 'fulfilled') {
                  setShowForm(false);
                  afterChange?.();
                }
              });
            });
          }}
          create={true}
        />
      )}
    </div>
  );
};

const GetContentAuthorsComponent = ({ id }: { id: string }) => {
  const dispatch = useCommonDispatch();
  const [selectorValues, setSelectorValues] = useState<
    { label: string; value: string }[]
  >([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const { selectedContenrAuthorsStatus, selectedContentAuthors } =
    useContentSelector((state) => state.authors);
  const getAuthors = () => {
    dispatch(
      AuthorsActions.getContentEntityAuthorsAsync({
        ContentEntityId: id,
        page: 1,
        pageSize: 10,
      })
    );
  };
  const isDirty = () => {
    return !(
      Array.isArray(selectedContentAuthors) &&
      Array.isArray(selectorValues) &&
      selectedContentAuthors.length === selectorValues.length &&
      selectedContentAuthors.every(
        (v, i) => v.id?.toString() === selectorValues[i].value
      )
    );
  };
  useEffect(() => {
    getAuthors();
    return () => {
      dispatch(AuthorsActions.clearSelectedContentAuthors());
    };
  }, [id]);
  useEffect(() => {
    setSelectorValues(
      selectedContentAuthors.map((s) => ({
        label: s.name || '',
        value: s.id?.toString() || '',
      })) || []
    );
  }, [selectedContentAuthors]);
  const searchauthors = (val: string) => {
    return dispatch(AuthorsActions.getAuthorsLookUpsAsync(val)).then((res) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return res?.payload?.map((item: { id: number; name: string }) => ({
        label: item.name,
        value: item.id,
      }));
    });
  };

  const openModal = () => {
    setShowForm(true);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await Promise.all(
        (
          selectedContentAuthors
            ?.filter(
              (item) =>
                !selectorValues.find((i) => i.value === item.id?.toString())
            )
            ?.map((item) => {
              return dispatch(
                AuthorsActions.unAssignAuthortoContentAsync({
                  contentEntityId: id,
                  authorId: item.id || 0,
                })
              );
            }) || []
        ).concat(
          selectorValues
            ?.filter(
              (item) =>
                !selectedContentAuthors?.find(
                  (i) => i.id?.toString() === item.value
                )
            )
            .map((item) => {
              return dispatch(
                AuthorsActions.assignAuthortoContentAsync({
                  contentEntityId: id,
                  authorId: parseInt(item.value),
                  order: 1,
                })
              );
            })
        )
      );
      setSaving(false);
      getAuthors();
    } catch (error) {
      setSaving(false);
      getAuthors();
    }
  };

  return (
    <div className="col-span-2 md:col-span-1 ">
      <div className="flex justify-between">
        <InputLabel name={'authours'} text={'Authors'} />
        <HasPermissionWrapperComponent permissions={['31.1', '31.4']} multiple>
          <Button
            color="primary"
            Icon={PlusCircleIcon}
            type="text"
            onClick={() => openModal()}
          />
        </HasPermissionWrapperComponent>
      </div>
      <div className="flex space-s-1">
        <div className="w-full">
          <CallBackSelector
            defaultValue={selectedContentAuthors}
            value={selectorValues}
            onSearch={searchauthors}
            onChange={(name, value: any) => setSelectorValues(value)}
            isMulti={true}
            name={'contentAuthors'}
          />{' '}
        </div>
        <HasPermissionWrapperComponent permissions={['31.4', '31.5']}>
          <Button
            disabled={!isDirty()}
            loading={saving}
            title={'save'}
            onClick={() => onSave()}
          />
        </HasPermissionWrapperComponent>
      </div>
      {showForm && (
        <AuthorsModalComponent
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onSubmit={(data) => {
            if (data) {
              createAuthorMultilingual(data, (authorId) => {
                dispatch(
                  AuthorsActions.assignAuthortoContentAsync({
                    contentEntityId: id,
                    authorId: authorId,
                    order: 1,
                  })
                ).then((res) => {
                  if (res.meta.requestStatus === 'fulfilled') {
                    setShowForm(false);
                    getAuthors();
                  }
                });
              });
            }
          }}
        />
      )}
    </div>
  );
};

const GetContentTagsComponent = ({ id }: { id: string }) => {
  const contentDispatch = useContentDispatch();

  const [selectorValues, setSelectorValues] = useState<
    { label: string; value: string }[]
  >([]);
  const [showForm, setShowForm] = useState(false);

  const contentEntityTags = useContentSelector(
    ({ contentEntity }) => contentEntity.contentEntityTags
  );
  const setContentEntityTagsStatus = useContentSelector(
    ({ contentEntity }) => contentEntity.setContentEntityTags.status
  );

  const getContentEntityTags = useCallback(() => {
    contentDispatch(ContentEntityActions.getContentEntityTagsAsync(id));
  }, [id, contentDispatch]);

  const isDirty = () => {
    return !(
      Array.isArray(contentEntityTags.data) &&
      Array.isArray(selectorValues) &&
      contentEntityTags.data.length === selectorValues.length &&
      contentEntityTags.data.every(
        (v, i) => v.toString() === selectorValues[i].value
      )
    );
  };

  const mapContentEntityTags = useCallback(() => {
    return contentEntityTags.data.map((ct) => {
      return {
        value: ct,
        label: ct,
      };
    });
  }, [contentEntityTags.data]);

  useEffect(() => {
    getContentEntityTags();

    return () => {
      contentDispatch(ContentEntityActions.clearContentEntityTags());
    };
  }, [contentDispatch, getContentEntityTags]);

  useEffect(() => {
    const mappedContentEntityTags = mapContentEntityTags();

    setSelectorValues(mappedContentEntityTags || []);
  }, [mapContentEntityTags]);

  const searchTags = async (val: string) => {
    return contentDispatch(TagsActions.getTagsLookupsAsync()).then((res) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return res?.payload?.map((item: { id: number; name: string }) => ({
        label: item.name,
        value: item.id,
      }));
    });
  };

  const openModal = () => {
    setShowForm(true);
  };

  return (
    <div className="col-span-2 md:col-span-1 ">
      <div className="flex justify-between">
        <InputLabel name={'tags'} text={'Tags'} />
        <HasPermissionWrapperComponent permissions={['29.1', '27.31']} multiple>
          <Button
            color="primary"
            Icon={PlusCircleIcon}
            type="text"
            onClick={() => openModal()}
          />
        </HasPermissionWrapperComponent>
      </div>
      <div className="flex space-s-1">
        <div className="w-full">
          <CallBackSelector
            defaultValue={mapContentEntityTags()}
            value={selectorValues}
            onSearch={searchTags}
            onChange={(name, value: any) => setSelectorValues(value)}
            isMulti={true}
            name={'contentEntityTags'}
          />{' '}
        </div>
        <HasPermissionWrapperComponent permissions={['27.31']}>
          <Button
            disabled={!isDirty()}
            loading={setContentEntityTagsStatus === 'loading' && !showForm}
            title={'save'}
            onClick={async () => {
              const res = await contentDispatch(
                ContentEntityActions.setContentEntityTagsAsync({
                  contentEntityId: id,
                  tagIds: selectorValues.map((v) => v.value),
                })
              );

              if (res.meta.requestStatus === 'fulfilled') {
                getContentEntityTags();
              }
            }}
          />
        </HasPermissionWrapperComponent>
      </div>
      {showForm && (
        <TagModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={(data) => {
            if (data)
              contentDispatch(
                TagsActions.createTagAsync({
                  ...data,
                })
              ).then((res) => {
                if (res.meta.requestStatus === 'fulfilled') {
                  const payload = res.payload as { id: string; name: string };

                  contentDispatch(
                    ContentEntityActions.setContentEntityTagsAsync({
                      contentEntityId: id,
                      tagIds: [
                        ...selectorValues.map((v) => v.value),
                        payload.id,
                      ],
                    })
                  ).then((res) => {
                    if (res.meta.requestStatus === 'fulfilled') {
                      setShowForm(false);

                      getContentEntityTags();
                    }
                  });
                }
              });
          }}
        />
      )}
    </div>
  );
};

const GetContentPublishersComponent = ({
  id,
  publisherId,
  publisherName,
  onSubmit,
}: {
  id: string;
  publisherId: number | undefined;
  publisherName: string | undefined;
  onSubmit: () => void;
}) => {
  const [selectorValues, setSelectorValues] = useState<
    | {
      label: string | undefined;
      value: number | undefined;
    }
    | undefined
  >();
  const [showForm, setShowForm] = useState(false);
  const [forceSearch, setForceSearch] = useState(false);

  const setContentEntityPublisherState = useSetContentEntityPublisher();

  const searchTags = async (val: string) => {
    let lookups: { value: number | undefined; label: string | undefined }[] =
      [];

    await getPublishersLookups(val, (res) => {
      lookups = res.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    });

    return lookups;
  };

  const openModal = () => {
    setShowForm(true);
  };

  useEffect(() => {
    setSelectorValues(
      publisherId ? { value: publisherId, label: publisherName } : undefined
    );
  }, [publisherId]);

  return (
    <div className="col-span-2 md:col-span-1 ">
      <div className="flex justify-between">
        <InputLabel name={'publisher'} text={'Publisher'} />
        <HasPermissionWrapperComponent permissions={['29.1', '27.31']} multiple>
          <Button
            color="primary"
            Icon={PlusCircleIcon}
            type="text"
            onClick={() => openModal()}
          />
        </HasPermissionWrapperComponent>
      </div>
      <div className="flex space-s-1">
        <div className="w-full">
          <CallBackSelector
            value={selectorValues}
            onSearch={searchTags}
            onChange={(name, value: any) => setSelectorValues(value)}
            name={'publisher'}
            forceSearch={forceSearch}
            setForceSearch={setForceSearch}
            clear
          />{' '}
        </div>
        <HasPermissionWrapperComponent permissions={['152.1']}>
          <Button
            disabled={publisherId === selectorValues?.value}
            loading={
              setContentEntityPublisherState.status === 'loading' && !showForm
            }
            title={'save'}
            onClick={() => {
              setContentEntityPublisher(
                {
                  contentEntityId: id,
                  publisherId: selectorValues?.value,
                },
                onSubmit
              );
            }}
          />
        </HasPermissionWrapperComponent>
      </div>
      {showForm && (
        <PublisherModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={(res) => {
            setContentEntityPublisher(
              { contentEntityId: id, publisherId: res },
              () => {
                onSubmit();

                setShowForm(false);
                setForceSearch(true);
              }
            );
          }}
          isLoadingExternal={
            setContentEntityPublisherState.status === 'loading'
          }
        />
      )}
    </div>
  );
};

const GetContentVoiceActorsComponent = ({
  id,
  voiceActorId,
  voiceActorName,
  onSubmit,
}: {
  id: string;
  voiceActorId: string | undefined;
  voiceActorName: string | undefined;
  onSubmit: () => void;
}) => {
  const languageId = useCommonSelector(
    ({ common }) => common.settings.LanguageId
  );

  const [selectorValues, setSelectorValues] = useState<
    | {
      label: string | undefined;
      value: string | undefined;
    }
    | undefined
  >();
  const [showForm, setShowForm] = useState(false);
  const [forceSearch, setForceSearch] = useState(false);

  const setContentEntityVoiceActorState = useSetContentEntityVoiceActor();

  const searchTags = async (val: string) => {
    let lookups: { value: string | undefined; label: string | undefined }[] =
      [];

    await getVoiceActorsLookups(val, (res) => {
      lookups = res.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    });

    return lookups;
  };

  const openModal = () => {
    setShowForm(true);
  };

  useEffect(() => {
    setSelectorValues(
      voiceActorId ? { value: voiceActorId, label: voiceActorName } : undefined
    );
  }, [voiceActorId]);

  return (
    <div className="col-span-2 md:col-span-1 ">
      <div className="flex justify-between">
        <InputLabel name={'voiceActor'} text={'Voice Actor'} />
        <HasPermissionWrapperComponent permissions={['29.1', '27.33']} multiple>
          <Button
            color="primary"
            Icon={PlusCircleIcon}
            type="text"
            onClick={() => openModal()}
          />
        </HasPermissionWrapperComponent>
      </div>
      <div className="flex space-s-1">
        <div className="w-full">
          <CallBackSelector
            value={selectorValues}
            onSearch={searchTags}
            onChange={(name, value: any) => setSelectorValues(value)}
            name={'publisher'}
            forceSearch={forceSearch}
            setForceSearch={setForceSearch}
            clear
          />{' '}
        </div>
        <HasPermissionWrapperComponent permissions={['155.1']}>
          <Button
            disabled={voiceActorId === selectorValues?.value}
            loading={
              setContentEntityVoiceActorState.status === 'loading' && !showForm
            }
            title={'save'}
            onClick={() => {
              setContentEntityVoiceActor(
                {
                  contentEntityId: id,
                  voiceActorId: selectorValues?.value,
                  languageId,
                },
                onSubmit
              );
            }}
          />
        </HasPermissionWrapperComponent>
      </div>
      {showForm && (
        <VoiceActorModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={(res) => {
            setContentEntityVoiceActor(
              { contentEntityId: id, voiceActorId: res },
              () => {
                onSubmit();
                setShowForm(false);
                setForceSearch(true);
              }
            );
          }}
        />
      )}
    </div>
  );
};

const GetContentEntityListsComponent = ({ id }: { id: string }) => {
  const contentDispatch = useContentDispatch();

  const [selectorValues, setSelectorValues] = useState<
    {
      label: string | undefined;
      value: number | undefined;
      order: number | undefined;
    }[]
  >([]);
  const [showForm, setShowForm] = useState(false);

  const contentEntityListsByContentEntityId =
    useContentEntityListsByContentEntityId();
  const { status: setContentEntityListsItemListStatus } =
    useSetContentEntityListsItemList();

  const getContentEntityListsByContentEntityIdMemoized = useCallback(() => {
    getContentEntityListsByContentEntityId(id);
  }, [id, contentDispatch]);

  const isDirty = () => {
    return !(
      Array.isArray(contentEntityListsByContentEntityId.data) &&
      Array.isArray(selectorValues) &&
      contentEntityListsByContentEntityId.data.length ===
      selectorValues.length &&
      contentEntityListsByContentEntityId.data.every(
        (v, i) => v.listId?.toString() === selectorValues[i].value?.toString()
      )
    );
  };

  const mapContentEntityListsByContentEntityId = useCallback(() => {
    return contentEntityListsByContentEntityId.data.map((l) => {
      return {
        value: l.listId,
        label: l.referenceName,
        order: l.order,
      };
    });
  }, [contentEntityListsByContentEntityId.data]);

  useEffect(() => {
    getContentEntityListsByContentEntityIdMemoized();

    return () => {
      clearContentEntityListsByContentEntityId();
    };
  }, [contentDispatch, getContentEntityListsByContentEntityIdMemoized]);

  useEffect(() => {
    const mappedContentEntityListsByContentEntityId =
      mapContentEntityListsByContentEntityId();

    setSelectorValues(mappedContentEntityListsByContentEntityId || []);
  }, [mapContentEntityListsByContentEntityId]);

  const searchTags = async (val: string) => {
    return getContentEntityListsLookups({}).then((res) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return res?.payload?.map(
        (item: { id: number; referenceName: string }) => ({
          label: item.referenceName,
          value: item.id,
        })
      );
    });
  };

  const openModal = () => {
    setShowForm(true);
  };

  return (
    <div className="col-span-2 md:col-span-1 ">
      <div className="flex justify-between">
        <InputLabel name={'lists'} text={'Lists'} />
        {/* <HasPermissionWrapperComponent permissions={['31.1']}>
          <Button
            color="primary"
            Icon={PlusCircleIcon}
            type="text"
            onClick={() => openModal()}
          />
        </HasPermissionWrapperComponent> */}
      </div>
      <div className="flex space-s-1">
        <div className="w-full">
          <CallBackSelector
            defaultValue={mapContentEntityListsByContentEntityId()}
            value={selectorValues}
            onSearch={searchTags}
            onChange={(name, value: any) => {
              const mappedValue = value.map((v: any, i: number) => {
                return {
                  ...v,
                  order: i,
                };
              });

              setSelectorValues(mappedValue);
            }}
            isMulti={true}
            name={'contentEntityTags'}
          />{' '}
        </div>
        <HasPermissionWrapperComponent permissions={['145.9']}>
          <Button
            disabled={!isDirty()}
            loading={
              setContentEntityListsItemListStatus === 'loading' && !showForm
            }
            title={'save'}
            onClick={async () => {
              setContentEntityListsItemList(
                {
                  contentEntityId: id,
                  listIds: selectorValues.map((v) => ({
                    id: v.value,
                    order: v.order,
                  })),
                },
                getContentEntityListsByContentEntityIdMemoized
              );
            }}
          />
        </HasPermissionWrapperComponent>
      </div>
      {/* {showForm && (
        <ContentEntityListModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={(contentEntityListId) => {
            setContentEntityListsItemList(
              {
                contentEntityId: id,
                listIds: selectorValues
                  .map((v) => ({
                    id: v.value,
                    order: v.order,
                  }))
                  .concat([
                    { id: contentEntityListId, order: selectorValues.length },
                  ]),
              },
              getContentEntityListsByContentEntityIdMemoized
            );
          }}
        />
      )} */}
    </div>
  );
};

const GetContentBadgesComponent = ({ id }: { id: string }) => {
  const [selectedValue, setSelectedValue] = useState<
    { label: string | undefined; value: number | undefined } | undefined
  >();
  const [showForm, setShowForm] = useState(false);
  const [forceSearch, setForceSearch] = useState(false);

  const { data: contentEntityBadge, status: contentEntityBadgeStatus } =
    useGetContentEntityBadge();
  const { status: setContentEntityBadgeStatus } = useSetContentEntityBadge();

  useEffect(() => {
    getContentEntityBadge(id);

    return () => {
      clearContentEntityBadge();
    };
  }, [id]);

  useEffect(() => {
    setSelectedValue(
      contentEntityBadge
        ? {
          value: contentEntityBadge.id,
          label: contentEntityBadge.title,
        }
        : undefined
    );
  }, [contentEntityBadge]);

  const searchBadges = async (val: string) => {
    return getBadgesLookups(val).then((res) => {
      console.log(res);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return res?.payload?.map((item: { id: number; title: string }) => ({
        label: item.title,
        value: item.id,
      }));
    });
  };

  const openModal = () => {
    setShowForm(true);
  };

  const isDisabled = selectedValue?.value === contentEntityBadge?.id;

  return (
    <div className="col-span-2 md:col-span-1 ">
      <div className="flex justify-between">
        <InputLabel name={'badge'} text={'Badge'} />
        <HasPermissionWrapperComponent permissions={['29.1', '27.31']} multiple>
          <Button
            color="primary"
            Icon={PlusCircleIcon}
            type="text"
            onClick={() => openModal()}
          />
        </HasPermissionWrapperComponent>
      </div>
      <div className="flex space-s-1">
        <div className="w-full">
          <CallBackSelector
            defaultValue={
              contentEntityBadge
                ? {
                  value: contentEntityBadge.id,
                  label: contentEntityBadge.title,
                }
                : undefined
            }
            value={selectedValue}
            onSearch={searchBadges}
            onChange={(name, value: any) => setSelectedValue(value)}
            isMulti={false}
            name={'contentEntityBadge'}
            forceSearch={forceSearch}
            setForceSearch={setForceSearch}
          />{' '}
        </div>
        <HasPermissionWrapperComponent permissions={['27.33']}>
          <Button
            disabled={isDisabled}
            loading={setContentEntityBadgeStatus === 'loading' && !showForm}
            title={'save'}
            onClick={async () => {
              if (selectedValue?.value) {
                setContentEntityBadge(
                  {
                    contentEntityId: id,
                    badgeId: selectedValue.value,
                  },
                  () => {
                    getContentEntityBadge(id);
                  }
                );
              }
            }}
          />
        </HasPermissionWrapperComponent>
      </div>
      {showForm && (
        <BadgeModal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={(data) => {
            if (data) {
              createBadge(data, async (badgeId) => {
                setContentEntityBadge(
                  {
                    contentEntityId: id,
                    badgeId,
                  },
                  () => {
                    setShowForm(false);

                    getContentEntityBadge(id);

                    setForceSearch(true);
                  }
                );
              });
            }
          }}
        />
      )}
    </div>
  );
};

const CreateContentEntityDetails: React.FC<CreateContentItemFormPageProps> = ({
  allowedInEntityTypes,
  requireAuthor,
  id,
}) => {
  const lookups = useContentSelector((s) => s.contentEntityType.lookups);
  const dispatch = useCommonDispatch();

  const order = useContentSelector(
    ({ contentEntity }) => contentEntity.details?.adminOrder
  );
  const publisherId = useContentSelector(
    ({ contentEntity }) => contentEntity.details?.publisherId
  );
  const publisherName = useContentSelector(
    ({ contentEntity }) => contentEntity.details?.publisherName
  );

  const voiceActorId = useContentSelector(
    ({ contentEntity }) => contentEntity.details?.voiceActorId
  );
  const voiceActorName = useContentSelector(
    ({ contentEntity }) => contentEntity.details?.voiceActorName
  );

  const contentEntityType = useContentSelector(
    ({ contentEntityType }) => contentEntityType.details
  );

  const getData = () => {
    dispatch(
      ContentEntityActions.getContentparentsAsync({
        ContenteEntityId: id,
        page: 1,
        pageSize: 100,
        ContenteEntityType: '',
      })
    );
  };

  const getContentEntityData = () => {
    dispatch(ContentEntityActions.getContentEntityByIdAsync(id));
  };

  useEffect(() => {
    if (id) {
      getData();

      getContentEntityData();
    }
  }, [id]);

  return (
    <Card>
      <div className="space-y-8">
        <div className="">
          <h3 className="ms-2 lg:ms-0 text-2xl flex items-center align-middle font-bold space-s-2 tracking-tight leading-none  ">
            {<InnerPageToggleButton />}{' '}
            <div className="">
              <ReturnTranslatedText text="general.Details" />{' '}
            </div>
          </h3>
          {/* <p className=" text-sm text-gray-600">{type.description}</p> */}
        </div>
        <div className=" grid grid-cols-2 gap-4">
          {requireAuthor && <GetContentAuthorsComponent id={id} />}
          {allowedInEntityTypes?.map((item) => (
            <GetContentEntityComponent
              key={`${item.name} ${item.id}`}
              ContentItemType={item.name || ''}
              data={lookups.find((s) => s.name == item.name)}
              id={id}
              afterChange={getData}
            />
          ))}
          <GetContentTagsComponent id={id} />
          <GetContentEntityListsComponent id={id} />
          <GetContentBadgesComponent id={id} />
          {contentEntityType?.hasPublisher ? (
            <GetContentPublishersComponent
              id={id}
              publisherId={publisherId || undefined}
              publisherName={publisherName}
              onSubmit={() => {
                getContentEntityData();
              }}
            />
          ) : null}
          {contentEntityType?.hasVoiceActor ? (
            <GetContentVoiceActorsComponent
              id={id}
              voiceActorId={voiceActorId || undefined}
              voiceActorName={voiceActorName}
              onSubmit={() => {
                getContentEntityData();
              }}
            />
          ) : null}
          <ContentAdminOrder adminOrder={order ?? undefined} id={id} />
        </div>
      </div>
    </Card>
  );
};
export default CreateContentEntityDetails;

const ContentAdminOrder = ({
  adminOrder,
  id,
}: {
  adminOrder: number | undefined;
  id: string;
}) => {
  const contentDispatch = useContentDispatch();

  const [isAdminOrder, setIsAdminOrder] = useState<boolean | undefined>(adminOrder !== undefined);
  const [order, setOrder] = useState<number | undefined>(adminOrder);

  const permissions = useAuthSelector(({ auth }) => auth.permission);

  const setAdminOrderStatus = useContentSelector(
    ({ contentEntity }) => contentEntity.setAdminOrder.status
  );

  useEffect(() => {
    setOrder(adminOrder);
    setIsAdminOrder(adminOrder !== undefined);
  }, [adminOrder]);

  console.log({ isAdminOrder, order, adminOrder });
  console.log((isAdminOrder ? order : undefined) === order)

  return (
    <div>
      <div className="text-[16px]">
        <ReturnTranslatedText
          text={`general.${'Admin Order'}`}
          origintext="Admin Order"
        />
      </div>
      <div className="mt-2 flex justify-between items-center">
        <div className="flex justify-center items-center">
          <div className="pt-1">
            <CheckBox
              name="isAdminOrder"
              label={'Selected'}
              onClick={(value) => {
                setIsAdminOrder(value);
                if(value && order === undefined) {
                  setOrder(0);
                }
              }}
              checked={isAdminOrder}
              disabled={!permissions?.includes('27.23')}
            />
          </div>
          {isAdminOrder && (
            <div className="rtl:mr-3 ltr:ml-3">
              <NumberInput
                name="content-admin-order"
                onChange={(name, value) => {
                  setOrder(Number(value));
                }}
                value={order}
                min={0}
              />
            </div>
          )}
        </div>
        <HasPermissionWrapperComponent permissions={['27.23']}>
          <div className="w-[54px]">
            <ButtonHeadless
              paddingYStyle="py-1"
              isDisabled={(isAdminOrder ? order : undefined) === adminOrder}
              bgStyle={(isAdminOrder ? order : undefined) === adminOrder ? 'bg-[#D4D4D4]' : 'bg-primary'}
              action={async () => {
                const res = await contentDispatch(
                  ContentEntityActions.setAdminOrderAsync({
                    id,
                    adminOrder: isAdminOrder ? order : undefined,
                  })
                );

                if (res.meta.requestStatus == 'fulfilled') {
                  contentDispatch(
                    ContentEntityActions.getContentEntityByIdAsync(id)
                  );
                }
              }}
            >
              <div className="flex justify-center items-center ext-white text-[14px] text-white">
                <ReturnTranslatedText text="buttons.Save" origintext="Save" />
                {setAdminOrderStatus === 'loading' && (
                  <BiLoaderAlt className="rtl:mr-1 ltr:ml-1 animate-spin w-5" />
                )}
              </div>
              { }
            </ButtonHeadless>
          </div>
        </HasPermissionWrapperComponent>
      </div>
    </div>
  );
};
